import type {
  DocumentProcessingItem,
  DocumentStage,
  RetryAction,
} from '@/lib/api/document-processing';
import { formatCount, progressLabel, progressPercent } from '@/lib/utils/processing';

/**
 * What a document row says about processing (T-0159), from the documents
 * list's pipeline view. Pure, so the wording is tested once and shared by the
 * wizard's baseline-docs rows and the Documents screen.
 *
 *   uploading  "Uploading…"
 *   reading    "Reading 40% · 52 of 128 pages"   (or "Reading…" without a count)
 *   indexing   "Indexing 63% · 2,914 of 4,640 passages"
 *   ready      "Ready · 128 pages"
 *   failed     "Read 128 pages · search indexing failed" + the reason + Retry
 */

export type ProcessingTone = 'working' | 'done' | 'failed';

export interface ProcessingView {
  stage: DocumentStage;
  /** Short status for a chip: Uploading… / Reading / Indexing / Ready / Failed. */
  chip: string;
  tone: ProcessingTone;
  /** Bar fill (whole percent of a real count); null when not known or failed. */
  percent: number | null;
  /** The line under the name. */
  line: string;
  /** Plain reason: why it failed, or why the stage is being retried. */
  reason: string | null;
  /** The recorded technical reason, for a tooltip. */
  detail: string | null;
  retry: RetryAction | null;
  /** "search indexing" / "reading", for the Retry button's accessible name. */
  retryWhat: string | null;
}

const SHEET_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
]);

/** Pages of a PDF or Word file; sheets of a workbook. */
function pageNoun(mimeType: string): [string, string] {
  return SHEET_MIME_TYPES.has(mimeType.toLowerCase()) ? ['sheet', 'sheets'] : ['page', 'pages'];
}

function pagesPhrase(n: number, mimeType: string): string {
  const [one, many] = pageNoun(mimeType);
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

/** The stage, from the pipeline view or (older servers) from `process_status`. */
export function stageOf(doc: DocumentProcessingItem): DocumentStage {
  if (doc.stage) return doc.stage;
  switch (doc.process_status) {
    case 'uploading':
      return 'uploading';
    case 'pending':
      return 'reading';
    case 'extracted':
      return 'indexing';
    case 'indexed':
      return 'ready';
    default:
      return 'failed';
  }
}

export function processingView(doc: DocumentProcessingItem): ProcessingView {
  const stage = stageOf(doc);
  const progress = doc.progress ?? null;
  const pages = doc.page_count ?? null;
  const attemptNote = doc.attempt && doc.attempt > 1 ? ` · attempt ${doc.attempt}` : '';
  const none = { reason: null, detail: null, retry: null, retryWhat: null };

  switch (stage) {
    case 'uploading': {
      const percent = progress?.unit === 'bytes' ? progressPercent(progress) : null;
      return {
        stage,
        chip: 'Uploading…',
        tone: 'working',
        percent,
        line:
          percent !== null && progress
            ? `Uploading ${percent}% · ${formatBytes(progress.done)} of ${formatBytes(progress.total)}`
            : 'Uploading…',
        ...none,
      };
    }
    case 'reading': {
      const [one, many] = pageNoun(doc.mime_type);
      const counted = progress?.unit === 'pages' ? progress : null;
      return {
        stage,
        chip: 'Reading',
        tone: 'working',
        percent: progressPercent(counted),
        line: `${progressLabel('Reading', counted, one, many)}${attemptNote}`,
        ...none,
        reason: doc.retry_reason ?? null,
      };
    }
    case 'indexing': {
      const counted = progress?.unit === 'passages' ? progress : null;
      const line = counted
        ? progressLabel('Indexing', counted, 'passage')
        : pages !== null
          ? `Read ${pagesPhrase(pages, doc.mime_type)} · indexing for search…`
          : 'Indexing for search…';
      return {
        stage,
        chip: 'Indexing',
        tone: 'working',
        percent: progressPercent(counted),
        line: `${line}${attemptNote}`,
        ...none,
        reason: doc.retry_reason ?? null,
      };
    }
    case 'ready':
      return {
        stage,
        chip: 'Ready',
        tone: 'done',
        percent: 100,
        line: pages !== null ? `Ready · ${pagesPhrase(pages, doc.mime_type)}` : 'Ready',
        ...none,
      };
    case 'failed':
    default: {
      const failedStage = doc.failed_stage ?? null;
      const line =
        failedStage === 'indexing'
          ? pages !== null
            ? `Read ${pagesPhrase(pages, doc.mime_type)} · search indexing failed`
            : 'Search indexing failed'
          : failedStage === 'reading'
            ? 'Reading failed'
            : failedStage === 'uploading'
              ? 'Upload failed'
              : 'Processing failed';
      const retry = doc.retry_action ?? null;
      return {
        stage: 'failed',
        chip: 'Failed',
        tone: 'failed',
        percent: null,
        line,
        reason: doc.failure_reason ?? null,
        detail: doc.failure_detail ?? null,
        retry,
        retryWhat:
          retry === 'reindex' ? 'search indexing' : retry === 'reprocess' ? 'reading' : null,
      };
    }
  }
}
